import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
} from '@angular/core';
import { BaseComponent } from '@macs-frontend/core';
import { F } from '@macs-frontend/core/utils/fp-ts';
import { UUID } from '@macs-frontend/core/utils/uuid';
import { makeSelectable } from '@macs-frontend/decorators';
import { environment as env } from '@macs-frontend/env';
import { APIResponse } from '@macs-frontend/features/shared';
import { Event } from '@macs-gql';
import { Store } from '@ngrx/store';
import { makeObservable, toJS } from 'mobx';
import { action, observable } from 'mobx-angular';

export interface DataColumnItem {
  title: string;
  dataKey: string;
  dataType?: string;
  dataLinkUuid?: string;
  dataLinkTemplate?: string;
}

export interface DataCachingEvent extends Event {
  dataCache?: object;
}

@Component({
  selector: 'md-activity-log',
  templateUrl: 'activity-log.component.html',
  styleUrls: ['activity-log.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityLogComponent extends BaseComponent {
  private _targetUuid?: UUID | string;
  @Input()
  set targetUuid(targetUuid: UUID | string | undefined) {
    const targetChanged = targetUuid !== this._targetUuid;
    this._targetUuid = targetUuid;
    if (targetChanged) {
      this.loadPage(1);
    }
  }
  get targetUuid() {
    return this._targetUuid;
  }
  @Input()
  removePrefix = '';

  @Input()
  snapshotLinkTemplate = ''; // expamle: /company-management/financial/estimates/details/[uuid]

  @Input()
  dataColumns: DataColumnItem[] = [];

  @Input()
  currencySign = '';

  @observable
  events: Event[] = [];

  @observable
  currentPage = 1;

  @observable
  pageCount = 1;

  constructor(
    readonly cdr: ChangeDetectorRef,
    readonly store$: Store,
    private http: HttpClient,
  ) {
    super();
    makeObservable(this);
    makeSelectable(this, { cdr, store$ });
  }

  loadPage(page: number) {
    if (!this.targetUuid) {
      return;
    }
    this.http
      .get<APIResponse<Event[]>>(
        `${env.api_url}/user-activity?targetUuid=${this.targetUuid}&page=${page}`,
      )
      .subscribe(resp => this.loadPageResponseHandler(resp));
  }

  @action
  loadPageResponseHandler(resp: APIResponse<Event[]>) {
    this.events = resp.data;
    this.pageCount = resp.meta.pageCount;
    this.currentPage = resp.meta.page;
  }

  actionFormat(action: string) {
    return F.pipe(
      action,
      action => {
        if (this.removePrefix && action.startsWith(this.removePrefix)) {
          return action.substr(this.removePrefix.length);
        }
        return action;
      },
      action => action.replace(/_/g, ' '),
    );
  }

  getDataField(event: Event, dataKey: string | null) {
    const data = event.data;

    if (data === null || data === undefined || !dataKey) {
      return '';
    }
    return data[dataKey as any] || '';
  }

  getDataLink(event: Event, dataColumn: DataColumnItem) {
    return this.getLink(
      dataColumn.dataLinkTemplate || null,
      this.getDataField(event, dataColumn.dataLinkUuid || null),
    );
  }

  getLink(template: string | null, uuid: string | null) {
    if (!uuid || !template) {
      return null;
    }

    return template.replace('[uuid]', uuid);
  }

  formatChangesData(data: { key: string; value: any }[]) {
    if (!data || !Array.isArray(data)) {
      return [];
    }
    return data.map(({ key, value }) => {
      const formattedKey = this.camelCaseToWords(key);
      return `${formattedKey}: ${value}`;
    });
  }

  camelCaseToWords(input: string): string {
    return input
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
      .toLowerCase()
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  getSnapshotLink(event: Event) {
    if (!event.snapshotUuid) {
      return null;
    }

    return this.getLink(this.snapshotLinkTemplate, String(event.snapshotUuid));
  }

  get showCommentColumn() {
    return this.snapshotLinkTemplate || this.events.filter(e => e.comment).length;
  }

  protected readonly toJS = toJS;
}
